import React, { useState, useEffect } from "react";
import { Formik, Form, Field, useField } from "formik";
import * as Yup from "yup";
import emailjs from "emailjs-com";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Headline,
  Paragraph,
  Section,
  ContentBlock,
  Button,
  InlineLink,
} from "./ui";
import SmartImage from "./SmartImage";

// Helper function to parse compound spacing values
const parseSpacing = (spacing) => {
  // Check if it's a compound spacing like "comfortable_top_tight_bottom"
  if (spacing.includes("_top_") && spacing.includes("_bottom")) {
    const match = spacing.match(/^(.+)_top_(.+)_bottom$/);
    if (match) {
      return {
        isCompound: true,
        top: match[1],
        bottom: match[2],
      };
    }
  }
  return { isCompound: false, spacing };
};

// Individual spacing classes
const spacingTopClasses = {
  compact: "pt-8 sm:pt-12",
  comfortable: "pt-16 sm:pt-24",
  spacious: "pt-24 sm:pt-40",
  tight: "pt-4 sm:pt-6",
  none: "",
};

const spacingBottomClasses = {
  compact: "pb-8 sm:pb-12",
  comfortable: "pb-16 sm:pb-24",
  spacious: "pb-24 sm:pb-40",
  tight: "pb-4 sm:pb-6",
  none: "",
};

const MyTextInput = ({ label, darkMode, ...props }) => {
  const [field, meta] = useField(props);
  return (
    <>
      <label
        htmlFor={props.id || props.name}
        className={`block text-left text-sm font-medium mb-1 ${
          darkMode ? "text-[var(--color-primary)]" : "text-gray-700"
        }`}
      >
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          className={`w-full border rounded px-3 py-2 ${
            darkMode
              ? "border-gray-600 text-[var(--color-primary)] placeholder-gray-400 focus:border-[var(--color-primary)] focus:outline-none"
              : "border-gray-300 text-gray-900 bg-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
          }`}
          style={
            darkMode ? { backgroundColor: "var(--color-secondary)" } : undefined
          }
          {...field}
          {...props}
        />
        {meta.touched && meta.error ? (
          <div
            className="text-xs text-red-600"
            style={{
              position: "absolute",
              top: "0.5rem",
              right: "0.75rem",
              fontSize: "12px",
            }}
          >
            {meta.error}
          </div>
        ) : null}
      </div>
    </>
  );
};

const MyCheckbox = ({ children, darkMode, ...props }) => {
  const [field, meta] = useField({ ...props, type: "checkbox" });
  return (
    <div className="flex items-start gap-2 mb-4">
      <input
        {...field}
        {...props}
        type="checkbox"
        className={`mt-1 ${
          darkMode
            ? "accent-[var(--color-primary)] bg-[var(--color-secondary)] border-gray-600"
            : "accent-blue-600"
        }`}
      />
      <div className="flex-1">
        {children}
        {meta.touched && meta.error ? (
          <div className="text-xs text-red-600 mt-1">{meta.error}</div>
        ) : null}
      </div>
    </div>
  );
};

const CallToActionWithModal = ({
  darkMode = false,
  headline,
  paragraph,
  spacing = "spacious",
  margin = true,
  marginTop = 0,
}) => {
  const [showModal, setShowModal] = useState(false);
  const parsedSpacing = parseSpacing(spacing);

  // Block background scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showModal]);

  // Build custom spacing class if compound spacing is used
  const customSpacingClass = parsedSpacing.isCompound
    ? `${spacingTopClasses[parsedSpacing.top] || ""} ${
        spacingBottomClasses[parsedSpacing.bottom] || ""
      }`
    : "";

  return (
    <Section
      variant={darkMode ? "primary" : "default"}
      spacing={parsedSpacing.isCompound ? "none" : spacing}
      className={`transition-colors duration-300 ${customSpacingClass}`}
      style={{
        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
        marginTop: `${marginTop}px`,
      }}
      margin={margin}
    >
      <ContentBlock
        maxWidth="4xl"
        align="left"
        className="mx-auto"
        margin={margin}
      >
        <Headline
          variant="h2"
          color={darkMode ? "secondary" : "primary"}
          className="text-left mb-4"
        >
          {headline || "Ihre Porsche-Perfektion beginnt hier"}
        </Headline>
        <Paragraph
          variant="lead"
          color={darkMode ? "secondary" : "primary"}
          className="mb-8 text-left"
        >
          {paragraph ||
            "Lassen Sie uns gemeinsam Ihr Porsche-Projekt starten – ob Restauration, Motorenbau oder individuelle Optimierung."}
        </Paragraph>
        <div className="flex flex-col sm:flex-row  gap-4 my-12">
          <Button
            as="a"
            href="https://wa.me/4915756850714"
            target="_blank"
            rel="noopener noreferrer"
            variant="secondary"
            size="xl"
          >
            Jetzt über WhatsApp
          </Button>
          <Button
            onClick={() => setShowModal(true)}
            variant="primary"
            size="xl"
            style={
              darkMode
                ? {
                    backgroundColor: "white",
                    color: "var(--color-primary)",
                  }
                : {
                    backgroundColor: "var(--color-primary)",
                    color: "var(--color-secondary)",
                  }
            }
          >
            Rückruf anfordern
          </Button>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 mt-[50px] z-50 flex items-center justify-center bg-[var(--color-primary)] p-4">
            <div
              className="rounded-sm p-6 w-full max-w-md shadow-lg relative text-[var(--color-primary)]"
              style={{ backgroundColor: "var(--color-secondary-dark)" }}
            >
              <Headline variant="h4" className="mb-6 text-left" color="primary">
                Rückruf anfordern
              </Headline>
              <SmartImage
                src="gallery/workshop/workshop-outside_uw6i86"
                alt="WTM Sportwagen Werkstatt"
                width={400}
                height={200}
                className="w-full h-0 sm:h-40 object-cover rounded-sm mb-6"
              />
              <Formik
                initialValues={{
                  name: "",
                  phone: "",
                  time: "",
                  consent: false,
                }}
                validationSchema={Yup.object({
                  name: Yup.string()
                    .max(50, "Name darf maximal 50 Zeichen lang sein")
                    .required("Pflichtfeld"),
                  phone: Yup.string()
                    .matches(
                      /^[0-9\s\-\+\(\)]+$/,
                      "Bitte geben Sie eine gültige Telefonnummer ein"
                    )
                    .min(5, "Telefonnummer muss mindestens 5 Zeichen lang sein")
                    .required("Pflichtfeld"),
                  consent: Yup.boolean()
                    .oneOf([true], "Bitte zustimmen")
                    .required(
                      "Zustimmung zur Datenschutzerklärung ist erforderlich"
                    ),
                })}
                onSubmit={(values, { setSubmitting, resetForm }) => {
                  // Show success toast immediately
                  toast.success(
                    "Vielen Dank für Ihre Anfrage! Wir melden uns unverzüglich bei Ihnen."
                  );

                  // Send email via EmailJS
                  emailjs
                    .send(
                      "service_t0w7lhk",
                      "template_nbeag76",
                      {
                        from_name: values.name,
                        phone: values.phone,
                        time: values.time,
                        message: `Rückruf angefordert für ${
                          values.time || "keine spezifische Zeit angegeben"
                        }`,
                      },
                      "7yrBfJRP1uVs_QHMZ"
                    )
                    .then(
                      (result) => {
                        resetForm();
                        setShowModal(false);
                      },
                      (error) => {
                        console.error("Email sending failed:", error);
                        toast.error(
                          "Ein Fehler ist aufgetreten, bitte kontaktieren Sie uns direkt per Telefon oder WhatsApp."
                        );
                      }
                    )
                    .finally(() => {
                      setSubmitting(false);
                    });
                }}
              >
                {({
                  isSubmitting,
                  isValid,
                  values,
                  setFieldValue,
                  setTouched,
                }) => (
                  <Form>
                    <MyTextInput
                      label="Name"
                      name="name"
                      type="text"
                      placeholder="Ihr vollständiger Name"
                      className="mb-4"
                      darkMode={true}
                    />

                    <MyTextInput
                      label="Telefonnummer"
                      name="phone"
                      type="tel"
                      placeholder="0179 1234 5678"
                      className="mb-4"
                      darkMode={true}
                    />

                    <div className="flex flex-col gap-2">
                      <p className="text-sm text-[var(--color-primary)] text-left">
                        Bevorzugte Uhrzeit (optional)
                      </p>
                      <div className="flex w-full pb-4">
                        {["Vormittags", "Nachmittags", "Abends"].map(
                          (label, index) => (
                            <button
                              key={label}
                              type="button"
                              onClick={() => setFieldValue("time", label)}
                              className={`px-4 py-2 border text-sm ${
                                values.time === label
                                  ? "text-[var(--color-secondary)] border-[var(--color-primary)]"
                                  : "text-[var(--color-primary)] border-gray-600"
                              } ${
                                index === 0
                                  ? "rounded-l"
                                  : index === 2
                                  ? "rounded-r"
                                  : ""
                              } ${index > 0 ? "-ml-px" : ""}`}
                              style={{
                                backgroundColor:
                                  values.time === label
                                    ? "var(--color-primary)"
                                    : "var(--color-secondary)",
                              }}
                            >
                              {label}
                            </button>
                          )
                        )}
                      </div>
                    </div>

                    <MyCheckbox name="consent" darkMode={true}>
                      <p className="text-sm text-[var(--color-primary)] text-left">
                        Ich stimme der Verarbeitung meiner Daten zur Bearbeitung
                        der Anfrage zu.{" "}
                        <InlineLink to="/datenschutz" variant="primary">
                          Datenschutzerklärung
                        </InlineLink>
                      </p>
                    </MyCheckbox>

                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        onClick={() => setShowModal(false)}
                        variant="outline"
                        size="medium"
                        className="hover:bg-[var(--color-primary)]"
                        style={{
                          color: "var(--color-primary)",
                          borderColor: "var(--color-primary)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "var(--color-primary)";
                          e.currentTarget.style.color =
                            "var(--color-secondary)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color = "var(--color-primary)";
                        }}
                      >
                        Abbrechen
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        variant="secondary"
                        size="medium"
                        id="rueckruf-submit"
                        className={
                          !values.name || !values.phone || !values.consent
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }
                        onClick={(e) => {
                          if (
                            !values.name ||
                            !values.phone ||
                            !values.consent
                          ) {
                            e.preventDefault();
                            setTouched({
                              name: true,
                              phone: true,
                              consent: true,
                            });
                          }
                        }}
                      >
                        {isSubmitting ? "Wird gesendet..." : "Absenden"}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
              <button
                className="absolute top-1 right-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-4xl bg-transparent border-none"
                onClick={() => setShowModal(false)}
              >
                &times;
              </button>
            </div>
          </div>
        )}

        <ToastContainer />
      </ContentBlock>
    </Section>
  );
};

export default CallToActionWithModal;
